import type {
  TeamMemberWithUserDto,
  TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import {
  Permission,
  TeamsApi,
  WorkspaceMemberRole,
  WorkspaceMembersApi,
  WorkspacesApi,
  type WorkspaceMemberWithUserDto,
} from '@up2stay/up2promo-api-client';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@up2stay/up2promo-ui';
import { useIonViewWillEnter } from '@ionic/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { type RoleAssignment } from '@/core/authz/role-assignment';
import { SeatUsageIndicator } from '@/core/components/billing/quota-indicators';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { ListSearchField, ListToolbarSurface } from '@/core/components/layout';
import { MemberEditSheet } from '@/core/components/workspace/people/member-edit-sheet';
import {
  type MembersListSortOption,
  membersListSortToQuery,
} from '@/core/components/workspace/people/members-list-sort';
import { MembersListSortSelect } from '@/core/components/workspace/people/members-list-sort-select';
import { InviteMemberHeaderAction } from '@/core/components/workspace/users/invite-member-header-action';
import { InviteUserDialog } from '@/core/components/workspace/users/invite-user-dialog';
import { memberDisplayName } from '@/core/components/workspace/users/member-identity';
import { RemoveMemberDialog } from '@/core/components/workspace/users/remove-member-dialog';
import { UsersTable } from '@/core/components/workspace/users/users-table';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useInvalidateWorkspacePermissions } from '@/core/hooks/use-permissions-query';
import { useWorkspaceMemberRole } from '@/core/hooks/use-workspace-member-role';
import { useWorkspaceRoles } from '@/core/hooks/use-workspace-roles';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';

export async function fetchWorkspaceMembers(
  workspaceId: string,
  page: number = 1,
  limit: number = 10,
  search?: string,
  sort: MembersListSortOption = 'joinedDesc',
): Promise<{
  data: WorkspaceMemberWithUserDto[];
  totalPages: number;
  hasNextPage: boolean;
}> {
  const { sortBy, sortOrder } = membersListSortToQuery(sort);
  const response = await WorkspaceMembersApi.findAll({
    path: {
      workspaceId,
    },
    query: {
      page,
      limit,
      sortBy,
      sortOrder,
      ...(search && search.trim() ? { search: search.trim() } : {}),
    },
  });
  return {
    data: response.data?.data || [],
    totalPages: response.data?.totalPages || 1,
    hasNextPage: response.data?.hasNextPage || false,
  };
}

export async function fetchTeamsByUserId(workspaceId: string): Promise<{
  namesByUserId: Record<string, string[]>;
  idsByUserId: Record<string, string[]>;
}> {
  const res = await TeamsApi.findAll({
    path: { workspaceId },
    query: { page: 1, limit: 100 },
  });
  const teams = (res.data?.data ?? []) as TeamWithCountsDto[];
  if (!teams.length) {
    return { namesByUserId: {}, idsByUserId: {} };
  }

  const membersByTeamId = await Promise.all(
    teams.map(async (team) => {
      try {
        const m = await TeamsApi.listMembers({
          path: { workspaceId, teamId: team.id },
        });
        return [
          team.id,
          team.name,
          (m.data ?? []) as TeamMemberWithUserDto[],
        ] as const;
      } catch {
        return [team.id, team.name, [] as TeamMemberWithUserDto[]] as const;
      }
    }),
  );

  const namesByUserId = new Map<string, string[]>();
  const idsByUserId = new Map<string, string[]>();

  for (const [teamId, teamName, members] of membersByTeamId) {
    for (const member of members) {
      const userId = member.userId?.trim();
      if (!userId) continue;

      const names = namesByUserId.get(userId) ?? [];
      if (!names.includes(teamName)) {
        namesByUserId.set(userId, [...names, teamName]);
      }

      const ids = idsByUserId.get(userId) ?? [];
      if (!ids.includes(teamId)) {
        idsByUserId.set(userId, [...ids, teamId]);
      }
    }
  }

  return {
    namesByUserId: Object.fromEntries(namesByUserId),
    idsByUserId: Object.fromEntries(idsByUserId),
  };
}

async function updateMemberRole(
  workspaceId: string,
  memberId: string,
  assignment: RoleAssignment,
): Promise<void> {
  const body =
    assignment.kind === 'custom'
      ? { customRoleId: assignment.customRoleId }
      : { role: assignment.role };

  await WorkspaceMembersApi.updateRole({
    path: {
      workspaceId,
      memberId,
    },
    body,
  });
}

async function removeMember(
  workspaceId: string,
  memberId: string,
): Promise<void> {
  await WorkspaceMembersApi.remove({
    path: {
      workspaceId,
      memberId,
    },
  });
}

interface PeopleMembersTabProps {
  onManageRoles: () => void;
}

export function PeopleMembersTab({ onManageRoles }: PeopleMembersTabProps) {
  const dict = useDictionary();
  const peopleDict = dict.workspace.people;
  const usersDict = dict.workspace.users;
  const currentWorkspace = useCurrentWorkspace();
  const { user: currentUser } = useAuthStore();

  const [members, setMembers] = useState<WorkspaceMemberWithUserDto[]>([]);
  const [teamsByUserId, setTeamsByUserId] = useState<Record<string, string[]>>(
    {},
  );
  const [teamIdsByUserId, setTeamIdsByUserId] = useState<
    Record<string, string[]>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [listSort, setListSort] = useState<MembersListSortOption>('joinedDesc');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<WorkspaceMemberWithUserDto | null>(null);

  const { role: currentUserRole } = useWorkspaceMemberRole();
  const { can } = usePermissions();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const invalidatePermissions = useInvalidateWorkspacePermissions();
  const { data: roles = [], refetch: refetchRoles } = useWorkspaceRoles(
    currentWorkspace?.id,
  );

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadMembers = useCallback(
    async (pageNum: number = page) => {
      if (!currentWorkspace) return;

      setIsLoading(true);
      try {
        const [result, teams] = await Promise.all([
          fetchWorkspaceMembers(
            currentWorkspace.id,
            pageNum,
            limit,
            debouncedSearchQuery,
            listSort,
          ),
          fetchTeamsByUserId(currentWorkspace.id).catch(() => ({
            namesByUserId: {},
            idsByUserId: {},
          })),
        ]);
        setMembers(result.data);
        setTotalPages(result.totalPages);
        setTeamsByUserId(teams.namesByUserId);
        setTeamIdsByUserId(teams.idsByUserId);
      } catch (err) {
        console.error('Failed to load members:', err);
        toast.error(usersDict.errors.loadFailed);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentWorkspace,
      debouncedSearchQuery,
      limit,
      listSort,
      page,
      usersDict.errors.loadFailed,
    ],
  );

  const listQueryKey = `${debouncedSearchQuery}\0${listSort}`;
  const [prevListQueryKey, setPrevListQueryKey] = useState(listQueryKey);
  if (listQueryKey !== prevListQueryKey) {
    setPrevListQueryKey(listQueryKey);
    setPage(1);
  }

  useEffect(() => {
    if (!currentWorkspace) return;
    void (async () => {
      await loadMembers(page);
    })();
  }, [currentWorkspace, loadMembers, page]);

  useIonViewWillEnter(() => {
    void loadMembers(page);
  });

  const handleInviteSuccess = () => {
    void loadMembers();
  };

  const handleMemberClick = (member: WorkspaceMemberWithUserDto) => {
    setSelectedMember(member);
    setEditSheetOpen(true);
  };

  const handleEditSuccess = () => {
    invalidatePermissions(currentWorkspace?.id);
    void refetchRoles();
    void loadMembers();
  };

  const handleRemove = (member: WorkspaceMemberWithUserDto) => {
    setSelectedMember(member);
    setRemoveDialogOpen(true);
  };

  const handleRemoveSuccess = () => {
    invalidatePermissions(currentWorkspace?.id);
    void loadMembers();
  };

  const handleResendInvite = async (member: WorkspaceMemberWithUserDto) => {
    const email = member.user?.email ?? member.email;
    if (!currentWorkspace?.id || !email) return;
    try {
      const body = member.customRoleId
        ? {
            email,
            customRoleId: member.customRoleId,
            acceptRedirectUrl: buildRedirectUrl('/workspace/invitation/accept'),
            declineRedirectUrl: buildRedirectUrl(
              '/workspace/invitation/reject',
            ),
          }
        : {
            email,
            role: member.role,
            acceptRedirectUrl: buildRedirectUrl('/workspace/invitation/accept'),
            declineRedirectUrl: buildRedirectUrl(
              '/workspace/invitation/reject',
            ),
          };
      await WorkspacesApi.inviteUser({
        path: { id: currentWorkspace.id },
        body,
        headers: {
          origin: typeof window !== 'undefined' ? window.location.origin : '',
          referer: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
      toast.success(usersDict.inviteDialog.resendSuccess);
    } catch {
      toast.error(usersDict.inviteDialog.error);
    }
  };

  const canInvite = can(Permission.MEMBERS_INVITE);
  const inviteEntitled = isFeatureEnabled('feature.invite_members');

  if (!currentWorkspace) {
    return (
      <p className="text-[13.5px] leading-relaxed text-muted-foreground">
        {usersDict.selectWorkspace}
      </p>
    );
  }

  const getUserDisplayName = (member: WorkspaceMemberWithUserDto): string => {
    return memberDisplayName(member, usersDict.common.unknownUser);
  };

  return (
    <div className="flex flex-1 flex-col gap-4 md:gap-5">
      {canInvite && !entitlementsLoading && !inviteEntitled ? (
        <UpgradeCard feature="feature.invite_members" compact />
      ) : null}
      <ListToolbarSurface variant="card">
        {/* Mobile stacks search, sort and invite in full-width rows (the
            search was ~106px wide and invite icon-only, DEV-538). */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <ListSearchField
            className="min-w-0 sm:flex-1"
            variant="toolbar"
            type="text"
            placeholder={usersDict.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
          <MembersListSortSelect
            value={listSort}
            onChange={setListSort}
            labels={{
              sort: usersDict.sort,
              options: usersDict.sortOptions,
            }}
          />
          {canInvite ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap [&>*:last-child]:grow sm:[&>*:last-child]:grow-0">
              <SeatUsageIndicator />
              <InviteMemberHeaderAction
                onInvite={() => setInviteDialogOpen(true)}
              />
            </div>
          ) : null}
        </div>
      </ListToolbarSurface>

      <UsersTable
        members={members}
        roles={roles}
        teamsByUserId={teamsByUserId}
        isLoading={isLoading}
        currentUserId={currentUser?.id}
        currentUserRole={currentUserRole}
        onMemberClick={handleMemberClick}
        onEditRole={handleMemberClick}
        onRemove={handleRemove}
        onResendInvite={(member) => {
          void handleResendInvite(member);
        }}
        searchQuery={debouncedSearchQuery}
      />

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                size="md"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) {
                    setPage(page - 1);
                  }
                }}
                className={
                  page <= 1
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer'
                }
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNum) => {
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        href="#"
                        size="icon"
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(pageNum);
                        }}
                        isActive={pageNum === page}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                } else if (pageNum === page - 2 || pageNum === page + 2) {
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              },
            )}
            <PaginationItem>
              <PaginationNext
                href="#"
                size="md"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) {
                    setPage(page + 1);
                  }
                }}
                className={
                  page >= totalPages
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        workspaceId={currentWorkspace.id}
        roles={roles}
        currentUserRole={currentUserRole!}
        onSuccess={handleInviteSuccess}
        dict={{
          ...usersDict.inviteDialog,
          roleSelect: usersDict.roleSelect,
          cancel: usersDict.common.cancel,
          invalidEmail: usersDict.common.invalidEmail,
        }}
      />

      {selectedMember && currentUserRole && (
        <>
          <MemberEditSheet
            open={editSheetOpen}
            onOpenChange={setEditSheetOpen}
            workspaceId={currentWorkspace.id}
            member={selectedMember}
            roles={roles}
            currentUserId={currentUser?.id}
            currentUserRole={currentUserRole}
            initialTeamIds={teamIdsByUserId[selectedMember.userId ?? ''] ?? []}
            initialTeamNames={teamsByUserId[selectedMember.userId ?? ''] ?? []}
            dict={{
              title: peopleDict.memberSheet.title,
              description: peopleDict.memberSheet.description,
              roleLabel: usersDict.editRoleDialog.roleLabel,
              roleDescription: usersDict.editRoleDialog.roleDescription,
              ownerRoleLockedSelf: usersDict.editRoleDialog.ownerRoleLockedSelf,
              ownerRoleLocked: usersDict.editRoleDialog.ownerRoleLocked,
              teamsLabel: peopleDict.memberSheet.teamsLabel,
              teamsDescription: peopleDict.memberSheet.teamsDescription,
              noTeams: peopleDict.memberSheet.noTeams,
              manageRoles: peopleDict.memberSheet.manageRoles,
              createRole: peopleDict.memberSheet.createRole,
              save: peopleDict.memberSheet.save,
              saving: peopleDict.memberSheet.saving,
              cancel: usersDict.common.cancel,
              success: usersDict.editRoleDialog.success,
              teamsSuccess: peopleDict.memberSheet.teamsSuccess,
              error: usersDict.editRoleDialog.error,
              teamsError: peopleDict.memberSheet.teamsError,
              cannotChangeOwner: usersDict.editRoleDialog.cannotChangeOwner,
              transferOwnership: usersDict.editRoleDialog.transferOwnership,
              remove: usersDict.actions.remove,
              roleSelect: usersDict.roleSelect,
              editRoleDialog: {
                update: usersDict.editRoleDialog.update,
                updating: usersDict.editRoleDialog.updating,
                success: usersDict.editRoleDialog.success,
                error: usersDict.editRoleDialog.error,
              },
              roleEditor: dict.workspace.roles.editor,
            }}
            permissionsDict={dict.permissions as Record<string, unknown>}
            builtinRoleLabels={usersDict.roles}
            builtinRoleDescriptions={usersDict.roleDescriptions}
            onManageRoles={onManageRoles}
            onRemove={
              can(Permission.MEMBERS_REMOVE) &&
              selectedMember.userId !== currentUser?.id &&
              selectedMember.role !== WorkspaceMemberRole.OWNER
                ? () => handleRemove(selectedMember)
                : undefined
            }
            onSuccess={handleEditSuccess}
            updateMemberRole={updateMemberRole}
          />

          <RemoveMemberDialog
            open={removeDialogOpen}
            onOpenChange={setRemoveDialogOpen}
            workspaceId={currentWorkspace.id}
            memberId={selectedMember.id}
            memberName={getUserDisplayName(selectedMember)}
            onSuccess={handleRemoveSuccess}
            dict={usersDict.removeDialog}
            removeMember={removeMember}
          />
        </>
      )}
    </div>
  );
}
